// Login da Meta pelo SDK oficial do navegador.
//
// Estava dentro de MetaLoginButtons; saiu para cá quando o modal de conexão
// passou a oferecer o mesmo login por canal. Duas cópias do fluxo de OAuth
// seriam dois lugares para corrigir quando a Meta mudar de versão.

import api from "../services/api";
import { getFacebookAppId, getFacebookConfigId } from "../config";

const ESCOPO_FACEBOOK = [
  "pages_show_list",
  "pages_messaging",
  "pages_manage_metadata",
  "pages_read_engagement",
  "business_management"
].join(",");

// A API do Instagram tem duas variantes, com permissões distintas:
//
//   login do Instagram  -> instagram_business_*, app próprio, login em
//                          instagram.com
//   login do Facebook   -> instagram_basic + instagram_manage_messages, conta
//                          ligada a uma Página, token da Página
//
// Aqui é a segunda: quem responde no Direct é a Página dona da conta, e é o
// token dela que o webhook e a Send API usam. Pedir as `instagram_business_*`
// neste fluxo devolve "Invalid Scopes".
const ESCOPO_INSTAGRAM = [
  "instagram_basic",
  "instagram_manage_messages",
  ESCOPO_FACEBOOK
].join(",");

// Coexistência: o número segue atendido no aplicativo do celular e, ao mesmo
// tempo, pela Cloud API. Sem whatsapp_business_management não dá para assinar
// os campos que trazem o que foi respondido pelo aparelho.
const ESCOPO_WHATSAPP = [
  "whatsapp_business_messaging",
  "whatsapp_business_management",
  "business_management"
].join(",");

export const ESCOPOS = {
  facebook: ESCOPO_FACEBOOK,
  instagram: ESCOPO_INSTAGRAM,
  whatsapp: ESCOPO_WHATSAPP
};

export const carregarSdkMeta = (appId) =>
  new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({ appId, cookie: true, xfbml: false, version: "v21.0" });
      resolve(window.FB);
    };

    const script = document.createElement("script");

    script.src = "https://connect.facebook.net/pt_BR/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => reject(new Error("Falha ao carregar o SDK da Meta"));

    document.body.appendChild(script);
  });

// O que a tela precisa saber para desenhar (ou esconder) o botão de login.
// Sem appId não há login possível — e o `.env` fica só como reserva, porque
// bastava esquecer de repassá-lo ao contêiner para os botões sumirem sem erro.
export const buscarAppConfigMeta = async () => {
  try {
    const { data } = await api.get("/meta/app-config");

    return {
      appId: data?.appId || getFacebookAppId(),
      configIds: data?.configIds || null
    };
  } catch {
    return { appId: getFacebookAppId(), configIds: null };
  }
};

// Abre a janela da Meta e manda o resultado ao backend. Devolve `false` quando
// a pessoa fecha sem autorizar — não é erro, só não conectou.
export const conectarPelaMeta = async ({ canal, appId, configIds }) => {
  const FB = await carregarSdkMeta(appId);

  // App de Login for Business referencia a configuração criada no painel; app
  // do login clássico continua pedindo as permissões soltas. Misturar os dois
  // faz a Meta recusar com "Invalid Scopes".
  const configId = configIds?.[canal] || getFacebookConfigId(canal);

  const resposta = await new Promise((resolve) =>
    FB.login(
      resolve,
      configId
        ? { config_id: configId, response_type: "token" }
        : { scope: ESCOPOS[canal], return_scopes: true }
    )
  );

  if (!resposta?.authResponse) return false;

  const { userID, accessToken } = resposta.authResponse;

  await api.post("/facebook", {
    facebookUserId: userID,
    facebookUserToken: accessToken,
    addInstagram: canal === "instagram",
    addWhatsapp: canal === "whatsapp"
  });

  return true;
};
